import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { SimpleModal } from "../../components/SimpleModal";
import { Plus, Trash2, Wrench, AlertCircle, Edit, Phone, Mail, Building2 } from "lucide-react";

export default function CompanyInstallers() {
  const [installers, setInstallers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/client/installers");
      setInstallers(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setError(null);
    try {
      const payload = {
        first_name: editing.first_name,
        last_name: editing.last_name,
        company_name: editing.company_name || null,
        email: editing.email || null,
        phone: editing.phone || null,
      };
      if (editing.id) await api.put(`/client/installers/${editing.id}`, payload);
      else await api.post("/client/installers", payload);
      setEditing(null);
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le contact ${p.first_name} ${p.last_name} ?`)) return;
    try {
      await api.delete(`/client/installers/${p.id}`);
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  return (
    <div data-testid="company-installers-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="lc-small-label mb-1">Espace administrateur</div>
          <h1 className="display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <Wrench className="w-7 h-7 text-[var(--lc-orange)]" /> Poseurs &amp; monteurs
          </h1>
          <p className="text-sm text-[var(--lc-gray)] mt-2">Liste de contacts informative — ces contacts n'ont pas accès à la plateforme.</p>
        </div>
        <button
          onClick={() => setEditing({ first_name: "", last_name: "", company_name: "", email: "", phone: "" })}
          data-testid="company-add-installer"
          className="lc-btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nouveau contact
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading && <div className="text-sm text-[var(--lc-gray)]">Chargement…</div>}
      {!loading && installers.length === 0 && (
        <div className="lc-card p-10 text-center" data-testid="no-installers">
          <div className="text-base font-semibold mb-1">Aucun contact</div>
          <p className="text-sm text-[var(--lc-gray)]">Ajoutez vos poseurs et monteurs affiliés pour les retrouver facilement.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3" data-testid="installers-list">
        {installers.map((p) => (
          <div key={p.id} data-testid={`installer-row-${p.id}`} className="lc-card p-4 flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold">{p.first_name} {p.last_name}</div>
              {p.company_name && (
                <div className="text-xs font-semibold text-[var(--lc-orange)] mt-0.5 inline-flex items-center gap-1" data-testid={`installer-company-${p.id}`}>
                  <Building2 className="w-3 h-3" />{p.company_name}
                </div>
              )}
              <div className="text-xs text-[var(--lc-gray)] mt-1 flex flex-col gap-0.5">
                {p.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 hover:text-[var(--lc-orange)]"><Phone className="w-3 h-3" />{p.phone}</a>}
                {p.email && <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 hover:text-[var(--lc-orange)]"><Mail className="w-3 h-3" />{p.email}</a>}
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEditing({ ...p })} data-testid={`installer-edit-${p.id}`} className="p-1.5 hover:bg-black/5 rounded"><Edit className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(p)} data-testid={`installer-delete-${p.id}`} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <SimpleModal title={editing.id ? "Modifier le contact" : "Nouveau contact"} onClose={() => setEditing(null)} onSave={save}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="lc-label">Prénom</label><input data-testid="installer-first-name" className="lc-input" value={editing.first_name} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} /></div>
            <div><label className="lc-label">Nom</label><input data-testid="installer-last-name" className="lc-input" value={editing.last_name} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="lc-label">Société</label><input data-testid="installer-company-name" className="lc-input" value={editing.company_name || ""} onChange={(e) => setEditing({ ...editing, company_name: e.target.value })} placeholder="Ex : Menuiserie Dupont" /></div>
            <div><label className="lc-label">Email</label><input data-testid="installer-email" type="email" className="lc-input" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
            <div><label className="lc-label">Téléphone</label><input data-testid="installer-phone" className="lc-input" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
          </div>
        </SimpleModal>
      )}
    </div>
  );
}
